/**
 * Страница уведомлений врача.
 * Отображает список уведомлений о флагах риска пациентов.
 * Поддерживает отметку прочитанных и фильтрацию.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../api/client';
import { Notification } from '../../types';

const PAGE_SIZE = 30;

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const navigate = useNavigate();

  const loadNotifications = async () => {
    try {
      const res = await notificationsAPI.getNotifications(showUnreadOnly, { limit: PAGE_SIZE });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
      setTotalNotifications(res.data.total ?? res.data.notifications.length);
    } catch (err) {
      console.error('Ошибка загрузки уведомлений:', err);
    }
  };

  const loadMore = async () => {
    try {
      const res = await notificationsAPI.getNotifications(showUnreadOnly, {
        limit: PAGE_SIZE,
        offset: notifications.length,
      });
      setNotifications([...notifications, ...res.data.notifications]);
      setTotalNotifications(res.data.total ?? totalNotifications);
    } catch (err) {
      console.error('Ошибка загрузки:', err);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [showUnreadOnly]);

  const handleMarkRead = async (id: number) => {
    await notificationsAPI.markRead(id);
    loadNotifications();
  };

  const handleMarkAllRead = async () => {
    await notificationsAPI.markAllRead();
    loadNotifications();
  };

  return (
    <div>
      <div style={styles.header}>
        <h1>Уведомления</h1>
        <div style={styles.actions}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox" checked={showUnreadOnly}
              onChange={(e) => setShowUnreadOnly(e.target.checked)}
            />
            Только непрочитанные ({unreadCount})
          </label>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} style={styles.markAllBtn}>
              Отметить все как прочитанные
            </button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <p style={{ color: '#6b7280', padding: 20 }}>Нет уведомлений</p>
      ) : (
        <>
          {notifications.map((n) => (
            <div
              key={n.id}
              style={{
                ...styles.card,
                backgroundColor: n.is_read ? 'var(--bg-card)' : 'var(--bg-info)',
                borderLeft: n.type === 'risk_alert'
                  ? '4px solid #dc2626'
                  : '4px solid #2563eb',
              }}
            >
              <div style={styles.cardHeader}>
                <h3 style={styles.title}>{n.title}</h3>
                <span style={styles.date}>
                  {new Date(n.created_at).toLocaleString('ru-RU')}
                </span>
              </div>
              <p style={styles.message}>{n.message}</p>
              <div style={styles.cardActions}>
                {n.related_patient_id && (
                  <button
                    style={styles.linkBtn}
                    onClick={() => navigate(`/doctor/patients/${n.related_patient_id}`)}
                  >
                    Открыть карту пациента
                  </button>
                )}
                {!n.is_read && (
                  <button
                    style={styles.readBtn}
                    onClick={() => handleMarkRead(n.id)}
                  >
                    Прочитано
                  </button>
                )}
              </div>
            </div>
          ))}
          {notifications.length < totalNotifications && (
            <button onClick={loadMore} style={styles.loadMoreBtn}>
              Загрузить ещё ({totalNotifications - notifications.length})
            </button>
          )}
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
  },
  actions: { display: 'flex', alignItems: 'center', gap: 16 },
  checkboxLabel: { fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  markAllBtn: {
    background: 'none', border: '1px solid var(--border-color)', padding: '6px 14px',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)',
  },
  card: {
    padding: 16, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', marginBottom: 10,
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 8,
  },
  title: { fontSize: 15, margin: 0, color: 'var(--text-primary)' },
  date: { fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  message: { fontSize: 14, color: 'var(--text-primary)', margin: 0 },
  cardActions: {
    display: 'flex', gap: 12, marginTop: 12,
  },
  linkBtn: {
    background: 'none', border: 'none', color: '#2563eb',
    cursor: 'pointer', fontSize: 13, padding: 0,
  },
  readBtn: {
    background: 'var(--bg-btn-secondary)', border: 'none', padding: '4px 12px',
    borderRadius: 6, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)',
  },
  loadMoreBtn: {
    width: '100%', padding: '12px', backgroundColor: 'var(--bg-btn-secondary)',
    color: 'var(--text-btn-secondary)', border: 'none', borderRadius: 8,
    cursor: 'pointer', fontSize: 14, fontWeight: 500, marginTop: 8,
  },
};

export default NotificationsPage;
